import { Haptics } from '@capacitor/haptics';
import { NativeAudio } from '@capgo/native-audio';
import { Howl } from 'howler';
import { device, logError, runAndLogErrors } from './app.ts';
import { getTimerLikeDurationText } from './date.ts';

export const ANDROID_REMINDER_SOUND = 'reminder.mp3';
export const CHIMES_1 = 'media/chimes_1.mp3';
export const BACKGROUND_MUSIC_PATH = 'media/background_music.mp3';
export const BREATH_IN_PATH = 'media/bell_1.mp3';
export const BREATH_OUT_PATH = 'media/bell_2.mp3';

export const WEBSITE_SUPPORTED_LANG = ['fr', 'es']; // supported website languages
export const EMAIL_ADDRESS = "hello@coherence-app.com";
export const WEBSITE_URL = "https://coherence-app.com";
export const APP_URL = "https://app.coherence-app.com";
export const INSTALL_URL = WEBSITE_URL + "/share";
export const DONATE_URL = "https://buymeacoffee.com/coherence";
export const WEBSITE_INFO_URL = "/information/";
export const FEEDBACK_URL = "https://docs.google.com/forms/d/e/1FAIpQLScpnRwoLSfFvwKTq4wR_oWHtNvDPTTa4dnXEyB8zyreOiv8MA/viewform?usp=sf_link";
export const ANDROID_STORE_URL = 'https://play.google.com/store/apps/details?id=com.genux.coherence&utm_source=app&utm_content=install_toast';
export const IOS_STORE_URL = 'https://apps.apple.com/app/cardiac-coherence-breathing/id6759764067';
export const NEWS_RSS_FEED_URL = WEBSITE_URL + "/rss.xml";
export const WORLD_MAP_URL = 'https://map.coherence-app.com';
export const STATS_API_URL = WORLD_MAP_URL + '/api/realtime';

const FADE_OUT_MS = 300;

export function getMediaUrl(url: string) {
  return url;
}

export class Timer {

  private name: string;
  private duration: number;
  private pausedTime: number | null = null;
  private startTime: number | null = null;

  /**
   * Creates a timer
   * @param name the timer name
   * @param durationMs how long the timer runs
   */
  constructor(name: string, durationMs: number) {
    this.name = name;
    this.duration = durationMs;
  }

  public start() {
    this.startTime = Date.now();
  }

  public stop() {
    if (!this.startTime) {
      console.warn(`Timer ${this.name} was not started`);
      return;
    }
    this.startTime = null;
    this.pausedTime = null;
  }

  public pause() {
    if (this.pausedTime) {
      console.warn(`Timer ${this.name} already paused`);
      return;
    }
    this.pausedTime = Date.now();
  }

  public isPaused() {
    return this.pausedTime != null;
  }

  public getName() {
    return this.name;
  }

  /**
   * @returns false when stop() was called. true when the timer is started and has no remning time.
   */
  public isFinished() {
    return this.isStarted() && this.getRemainingTimeMs() <= 0;
  }

  public isStarted() {
    return this.startTime != null;
  }

  public getStartTime(): number | null {
    return this.startTime;
  }

  public resume() {
    if (!this.startTime) {
      console.warn(`Timer ${this.name} was not started`);
      return;
    }
    if (!this.pausedTime) {
      console.warn(`Timer ${this.name} was not paused`);
      return;
    }
    const lostTime = Date.now() - this.pausedTime;
    this.startTime += lostTime; // increase that lost time in timer
    this.pausedTime = null; // resumed
  }

  public getElapsedTimeMs() {
    const now = Date.now();
    let stTime = this.startTime ? now - this.startTime : 0;
    if (this.pausedTime != null) {
      const lostTime = now - this.pausedTime;
      stTime -= lostTime;
    }
    return stTime;
  }

  /**
   * 
   * @returns the remaining time in milliseconds
   */
  public getRemainingTimeMs() {
    return this.duration - this.getElapsedTimeMs();
  }

  public getRemainingText() {
    return getTimerLikeDurationText(this.getRemainingTimeMs());
  }

  public getElapsedText() {
    return getTimerLikeDurationText(this.getElapsedTimeMs());
  }
}

/** 1. The Instance Interface (What the player can do) */
interface IMediaPlayer {
  url: string;
  loop: boolean;

  play(): Promise<void>;
  stop(): Promise<void>;
  init(): Promise<void>;
  prepareMedia(): Promise<void>;
  name(): string;
}

/**
 * Native Platform Media Player
 */
class NativeMediaPlayer implements IMediaPlayer {

  private static preloadedMedia = new Set<string>();

  url: string;
  loop: boolean;

  public constructor(url: string, loop: boolean) {
    this.url = url;
    this.loop = loop;
  }

  async play() {
    if (this.loop) {
      await NativeAudio.loop({
        assetId: this.url,
      });
    } else {
      await NativeAudio.play({
        assetId: this.url,
      });
    }
  }

  async stop() {
    await NativeAudio.stop({
      assetId: this.url,
      // TODO: bug when fading out: https://github.com/Cap-go/capacitor-native-audio/issues/274
      // fadeOut: FADE_OUT_MS > 0,
      // fadeOutDuration: FADE_OUT_MS > 0 ? FADE_OUT_MS : undefined
    });
  }

  async init() {
    await NativeAudio.configure({
      ignoreSilent: true,
      showNotification: false,
      backgroundPlayback: true,
      background: true,
      focus: true // override silent mode in IOS
    })
  }

  async prepareMedia() {
    // check if media has already been preloaded
    if (NativeMediaPlayer.preloadedMedia.has(this.url)) return;
    // preload media for the first time
    await NativeAudio.preload({
      assetId: this.url,
      assetPath: this.url
    });
    // save to already preloaded
    NativeMediaPlayer.preloadedMedia.add(this.url);
  }

  name(): string {
    return 'Native';
  }
}

/**
 * Web Platform Media Player
 */
class WebMediaPlayer implements IMediaPlayer {

  media?: Howl;
  url: string;
  loop: boolean;

  public constructor(url: string, loop: boolean) {
    this.url = url;
    this.loop = loop;
  }

  async play() {
    if (!this.media) return;
    this.media.volume(1.0); // reset in case it was faded out
    this.media.play();
  }

  async stop() {
    if (!this.media) return;
    // listen to the fade event and then stop
    this.media.once('fade', () => { this.media?.stop() });
    // fade out, then stop
    this.media.fade(this.media.volume(), 0, FADE_OUT_MS)
  }

  async init() {
    // nothing to do to init Howl
  }

  async prepareMedia() {
    // create the player instance
    this.media = new Howl({
      src: [this.url],
      loop: this.loop,
      onplayerror: (_, err) => {
        logError(`mediaplayer: Web play error for ${this.url}`, err);
      }
    });
  }

  name(): string {
    return 'Web';
  }
}

/**
 * A media player that can play sounds
 */
export class MediaPlayer {

  private static initPromise?: Promise<void>;

  player: IMediaPlayer
  playing: boolean;

  private constructor(player: IMediaPlayer) {
    this.player = player;
    this.playing = false;
  }

  /**
   * Creates a media player for a given url and loop option. 
   * The media is preloaded and ready to play when the promise resolves.F
   * @param url 
   * @param loop 
   * @returns 
   */
  static async create(url: string, loop: boolean): Promise<MediaPlayer> {
    // create the OS specific media player
    const mediaPlayer: IMediaPlayer = device.ios ? new NativeMediaPlayer(url, loop) : new WebMediaPlayer(url, loop);
    try {
      // setup audio system if not already done and wait for it to be ready
      if (!this.initPromise) {
        this.initPromise = this.initAudio(mediaPlayer);
        await this.initPromise;
      }
    } catch (err) {
      logError(`mediaplayer: error initializing audio`, err);
    }
    // prepare media and return the player
    const player = new MediaPlayer(mediaPlayer);
    await player.prepareMedia();
    return player;
  }

  /**
   * Initializes the audio system.
   */
  private static async initAudio(mediaPlayer: IMediaPlayer) {
    try {
      await mediaPlayer.init();
      console.debug(`mediaplayer: ${mediaPlayer.name()} player initialized`);
    } catch (err) {
      logError(`mediaplayer: error initializing audio`, err);
    }
  }

  private async prepareMedia() {
    try {
      await this.player.prepareMedia();
      console.debug(`mediaplayer: preloaded media ${this.player.url}`);
    } catch (error) {
      logError(`mediaplayer: error preparing media for ${this.player.url}`, error);
    }
  }

  async play() {
    if (this.playing) return; // already playing

    this.playing = true;
    console.debug(`mediaplayer:play ${this.player.url}, loop: ${this.player.loop}`);
    try {
      await this.player.play();
    } catch (error) {
      logError(`mediaplayer: error playing media for ${this.player.url}`, error);
      this.playing = false;
    }
  }

  async stop() {
    if (!this.playing) return; // not playing

    console.debug(`mediaplayer:stop ${this.player.url}`);
    try {
      await this.player.stop();
    } catch (error) {
      logError(`mediaplayer: error stopping media for ${this.player.url}`, error);
    } finally {
      this.playing = false;
    }
  }
}

export const vibrate = async (ms: number) => {
  // TODO should be checked only once but device not available before init
  const vibrateBrowserSupport = window.navigator && typeof window.navigator.vibrate !== 'undefined';
  const vibrateSupported = device.native ? true : vibrateBrowserSupport;

  if (!vibrateSupported) {
    console.warn("mediaplayer: Vibration is not supported on this platform");
    return;
  }
  try {
    if (device.native) {
      await Haptics.vibrate({ duration: ms })
    } else {
      window.navigator.vibrate(ms);
    }
    console.debug(`mediaplayer: vibrate for ${ms}ms`);
  } catch (err) {
    logError('mediaplayer: vibrate error', err);
  }
}

/**
* Self-adjusting interval to account for drifting
*
* @param {function} workFunc  Callback containing the work to be done
*                             for each interval
* @param {int}      interval  Interval speed (in milliseconds)
* @param {boolean}  runOnStart (true) to run workFunc() when the timer starts
**/
export class DriftCorrectingTimer {
  expected: null | number = null;
  timeout: null | number = null;
  interval: number;
  runOnStart: boolean;
  workFunc: () => void;
  pauseStart: null | number = null;

  constructor(workFunc: () => void, interval: number, runOnStart: boolean = true) {
    this.workFunc = workFunc;
    this.interval = interval;
    this.runOnStart = runOnStart;
  }

  public start() {
    if (this.timeout) {
      throw new Error('Timer already started');
    }
    // schedule the first interval
    this.schedule(this.interval, this.interval);

    if (this.runOnStart) {
      this.workFunc();
    }
  }

  public pause() {
    if (this.isPaused()) {
      console.warn('Timer already paused');
      return;
    }
    if (!this.isStarted()) {
      console.warn('Timer is not started');
      return;
    }
    this.pauseStart = Date.now();
    this.clearTimeout(); // cancel the next scheduled step while
    console.debug('timer: paused', this.workFunc.name);
  }

  private clearTimeout() {
    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = null;
    }
  }

  public resume() {
    console.debug('timer: resumed', this.workFunc.name);
    if (!this.isPaused()) {
      console.warn('Timer is not paused');
      return;
    }
    if (this.pauseStart == null || this.expected == null) {
      console.warn('Timer pause state is invalid');
      return;
    }

    const pausedDuration = Date.now() - this.pauseStart;
    const newExpected = this.expected + pausedDuration;
    const nextTimeout = Math.max(0, newExpected - Date.now());
    this.schedule(nextTimeout, pausedDuration);
    // set pause state to false and reset pause start time AFTER scheduling
    this.pauseStart = null;
  }

  public stop() {
    this.clearTimeout();
    this.expected = null;
    this.pauseStart = null;
  }

  /**
   * 
   * Schedules the next interval, adjusting the expected time if needed. 
   * 
   * @param timeout the timeout in ms to schedule for the next call to step()
   * @param nextExpectDelta 
   */
  protected schedule(timeout: number, nextExpectDelta: number) {
    if (this.expected == null) { // first schedule
      this.expected = Date.now();
    }
    this.expected += nextExpectDelta;

    if (timeout <= 0) {
      console.warn(`timer: schedule called with invalid timeout (${timeout}ms) for ${this.workFunc.name}, defaulting to 100ms`);
      timeout = 100;
    }
    // set the expected time for the next interval 
    // to calc drift when the timeout callback is called
    this.timeout = window.setTimeout(this.step.bind(this), timeout);
  }

  /**
   * 
   * @returns is started
   */
  public isStarted(): boolean {
    // has a timeout or paused (clears the timeout too)
    return this.timeout != null || this.pauseStart != null;
  }

  public isPaused(): boolean {
    return this.pauseStart != null;
  }

  protected step() {
    if (!this.isStarted() || this.expected == null) {
      console.warn('timer: step() called while not started or expected time is null');
      return; // invalid
    }
    // compute drift
    let drift = Date.now() - this.expected;

    // Kink: If the drift is greater than the interval, the browser has throttled the timer.
    // Reset the expected timeline to 'now' so we don't fall into a negative loop.
    if (drift > this.interval) {
      console.warn('timer: browser throttling detected, reseting drift');
      this.expected = Date.now();
      drift = 0;
    }

    // call client function
    this.workFunc();
    // check if stopped during workFunc
    if (!this.isStarted() || this.expected == null) return;
    // schedule the next interval, adjusting for drift
    const nextTimeout = Math.max(0, this.interval - drift)
    this.schedule(nextTimeout, this.interval);
  }
}

export class DualDriftCorrectingTimer extends DriftCorrectingTimer {

  private useSecondInterval: boolean = false;
  private firstInterval: number;
  private secondInterval: number;

  constructor(workFunc: () => void, firstInterval: number, secondInterval: number, runOnStart: boolean = true) {
    super(workFunc, firstInterval, runOnStart);
    this.firstInterval = firstInterval;
    this.secondInterval = secondInterval;
  }

  protected schedule(timeout: number, nextExpectDelta: number) {
    // schedule as expected by the call
    super.schedule(timeout, nextExpectDelta);
    const shouldFLip = !this.isPaused() // only flip if the timer is not paused
    if (shouldFLip) {
      // update the internal interval AFTER scheduling to alternate between the two intervals
      // and alternate between two timeouts before the call to step() to create a dual interval effect
      this.useSecondInterval = !this.useSecondInterval;
      this.interval = this.useSecondInterval ? this.secondInterval : this.firstInterval;
    }
  }

  public stop() {
    super.stop();
    this.useSecondInterval = false; // reset to first interval when stopped
  }
}

export function setFullScreen(full: boolean) {
  if (!document.fullscreenEnabled) {
    console.warn('full screen is not enabled for browser')
    return;
  }

  runAndLogErrors(() => {
    if (full) {
      if (!document.fullscreenElement) { // already fullscreen
        if (typeof document.documentElement.requestFullscreen === 'function') {
          document.documentElement.requestFullscreen().catch((err) => logError('requestFullscreen() error', err));
        }
      } else {
        logError('setFullScreen(): Already fullscreen');
      }
    } else {
      if (document.exitFullscreen && document.fullscreenElement) {
        document.exitFullscreen().catch((err) => logError('exitFullscreen() error', err));
      }
    }
  });
}

/**
 * Pause animations on a all children of a DOM element
 * 
 */
export const setAnimationsEnabled = (el: HTMLElement, enabled: boolean): void => {
  if (!el) {
    console.error(`Element is null or undefined`, el);
    return;
  }
  // update all (CSS only) animations on the element and its children
  const elements = [el, ...el.querySelectorAll<HTMLElement>('*')];
  elements.map(el => {
    el.style.animationPlayState = enabled ? "running" : "paused";
  });
  console.debug(`setAnimationEnabled(${enabled}): ${elements.length}`);
}

